import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"
import {
  detectCategoryFromFilename,
  formatFileSize,
  isValidFileType,
} from "./active_storage/upload_helpers"

// Dropzone upload for job file intake (single or multi-file batch).
export default class extends Controller {
  static targets = [
    "input",
    "progress",
    "progressText",
    "error",
    "success",
    "dropzone",
    "dropping",
    "fileList",
    "fileRowTemplate",
    "processing",
    "maxFilesError",
  ]
  static values = {
    url: String,
    acceptedTypes: { type: String, default: "application/pdf" },
    multiple: { type: Boolean, default: false },
    maxFiles: { type: Number, default: 0 },
    detectCategory: { type: Boolean, default: false },
  }

  connect() {
    this.uploadedFiles = []
    this.nextFileIndex = 0

    this.setupDragAndDrop()
    if (this.hasInputTarget) {
      this.inputTarget.addEventListener("change", this.handleFileSelect.bind(this))
    }
  }

  setupDragAndDrop() {
    if (!this.hasDropzoneTarget) return

    const dropzone = this.dropzoneTarget

    ;["dragenter", "dragover", "dragleave", "drop"].forEach((eventName) => {
      dropzone.addEventListener(eventName, this.preventDefaults, false)
      document.body.addEventListener(eventName, this.preventDefaults, false)
    })

    this.boundHighlight = this.highlight.bind(this)
    this.boundUnhighlight = this.unhighlight.bind(this)

    ;["dragenter", "dragover"].forEach((eventName) => {
      dropzone.addEventListener(eventName, this.boundHighlight, false)
    })

    ;["dragleave", "drop"].forEach((eventName) => {
      dropzone.addEventListener(eventName, this.boundUnhighlight, false)
    })

    dropzone.addEventListener("drop", this.handleDrop.bind(this), false)
  }

  disconnect() {
    if (!this.hasDropzoneTarget) return

    const dropzone = this.dropzoneTarget
    ;["dragenter", "dragover", "dragleave", "drop"].forEach((eventName) => {
      dropzone.removeEventListener(eventName, this.preventDefaults, false)
      document.body.removeEventListener(eventName, this.preventDefaults, false)
      dropzone.removeEventListener(eventName, this.boundHighlight, false)
      dropzone.removeEventListener(eventName, this.boundUnhighlight, false)
    })
  }

  openFilePicker() {
    if (this.hasInputTarget) this.inputTarget.click()
  }

  preventDefaults(event) {
    event.preventDefault()
    event.stopPropagation()
  }

  highlight() {
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.add("hidden")
    }
    if (this.hasDroppingTarget && this.droppingTarget.classList.contains("hidden")) {
      this.droppingTarget.classList.remove("hidden")
    }
  }

  unhighlight(event) {
    if (event.relatedTarget && this.element.contains(event.relatedTarget)) {
      return
    }

    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.remove("hidden")
    }
    if (this.hasDroppingTarget) {
      this.droppingTarget.classList.add("hidden")
    }
  }

  handleDrop(event) {
    const startedUpload = this.handleFiles(event.dataTransfer.files)

    if (startedUpload && this.hasDroppingTarget) {
      this.progressTarget.classList.remove("hidden")
      this.droppingTarget.classList.add("hidden")
    }
  }

  handleFileSelect(event) {
    this.handleFiles(event.target.files)
  }

  handleFiles(files) {
    if (files.length === 0) return false
    this.hideMaxFilesError()

    const validFiles = []
    for (const file of files) {
      if (!isValidFileType(file, this.acceptedTypesValue)) {
        this.showError("This file type isn't supported. Please upload a PDF.")
        return false
      }
      validFiles.push(file)
    }

    if (this.maxFilesValue > 0 && this.multipleValue) {
      const remaining = this.maxFilesValue - this.uploadedFiles.length
      if (remaining <= 0) {
        this.showMaxFilesError()
        return false
      }
      if (validFiles.length > remaining) {
        validFiles.splice(remaining)
        this.showMaxFilesError()
      }
    }

    if (this.multipleValue) {
      this.uploadMultipleFiles(validFiles)
    } else {
      this.uploadFile(validFiles[0])
    }

    if (this.hasInputTarget) {
      this.inputTarget.value = ""
    }

    return true
  }

  uploadMultipleFiles(files) {
    this.uploadQueue = files
    this.currentUploadIndex = 0

    this.showProgress()
    this.updateMultipleProgress()
    this.dispatch("uploading")
    this.uploadNextFile()
  }

  uploadNextFile() {
    if (this.currentUploadIndex >= this.uploadQueue.length) {
      this.showDropzone()
      this.dispatch("uploaded")
      return
    }

    const file = this.uploadQueue[this.currentUploadIndex]
    const fileIndex = this.nextFileIndex
    const upload = new DirectUpload(file, this.urlValue, {
      directUploadWillCreateBlobWithXHR: (xhr) => {
        xhr.upload.addEventListener("progress", (event) => {
          const fileProgress = event.loaded / event.total * 100
          this.updateMultipleProgress(fileProgress)
        })
      },
    })

    upload.create((error, blob) => {
      if (error) {
        this.showError(`Upload failed for ${file.name}. Please try again.`)
        console.error("Upload error:", error)
        return
      }

      this.nextFileIndex += 1
      this.uploadedFiles.push({ blob, file, index: fileIndex })
      this.createHiddenInput(blob, fileIndex, file)
      this.renderFileList()
      this.dispatchFilesChanged()
      this.currentUploadIndex += 1
      this.uploadNextFile()
    })
  }

  updateMultipleProgress(fileProgress = 0) {
    if (!this.hasProgressTextTarget) return

    const current = this.currentUploadIndex + 1
    const total = this.uploadQueue.length
    this.progressTextTarget.textContent = `Uploading ${current} of ${total}...`
  }

  createHiddenInput(blob, index, file) {
    const form = this.element.closest("form")
    if (!form) return

    const fileInput = document.createElement("input")
    fileInput.type = "hidden"
    fileInput.name = `job[job_files_attributes][${index}][file]`
    fileInput.value = blob.signed_id
    form.appendChild(fileInput)

    if (this.detectCategoryValue && file) {
      const categoryInput = document.createElement("input")
      categoryInput.type = "hidden"
      categoryInput.name = `job[job_files_attributes][${index}][category]`
      categoryInput.value = detectCategoryFromFilename(file.name)
      form.appendChild(categoryInput)
    }
  }

  renderFileList() {
    if (!this.hasFileListTarget) return

    while (this.fileListTarget.firstChild) {
      this.fileListTarget.removeChild(this.fileListTarget.firstChild)
    }

    this.uploadedFiles.forEach((upload) => {
      this.fileListTarget.appendChild(this.buildFileRow(upload))
    })
  }

  buildFileRow(upload) {
    if (this.hasFileRowTemplateTarget) {
      const row = this.fileRowTemplateTarget.content.firstElementChild.cloneNode(true)
      row.dataset.fileIndex = upload.index
      row.querySelector('[data-role="filename"]').textContent = upload.file.name
      row.querySelector('[data-role="filesize"]').textContent = formatFileSize(upload.file.size)
      row.querySelector('[data-role="remove"]').addEventListener("click", () => this.removeFile(upload.index))
      return row
    }

    const row = document.createElement("div")
    row.className = "flex items-center justify-between py-2 px-3 bg-gray-50 rounded-md text-sm"
    row.dataset.fileIndex = upload.index
    row.textContent = `${upload.file.name} (${formatFileSize(upload.file.size)})`
    return row
  }

  removeFile(fileIndex) {
    this.uploadedFiles = this.uploadedFiles.filter((file) => file.index !== fileIndex)

    const form = this.element.closest("form")
    if (form) {
      form.querySelector(`input[name="job[job_files_attributes][${fileIndex}][file]"]`)?.remove()
      form.querySelector(`input[name="job[job_files_attributes][${fileIndex}][category]"]`)?.remove()
    }

    this.renderFileList()
    this.dispatchFilesChanged()

    if (this.maxFilesValue > 0 && this.uploadedFiles.length < this.maxFilesValue) {
      this.hideMaxFilesError()
    }
  }

  resetState() {
    this.uploadedFiles = []
    this.uploadQueue = []
    this.currentUploadIndex = 0
    this.nextFileIndex = 0

    if (this.hasInputTarget) {
      this.inputTarget.value = ""
    }

    const form = this.element.closest("form")
    if (form) {
      form
        .querySelectorAll('input[name^="job[job_files_attributes]"][name$="[file]"]')
        .forEach((input) => {
          if (input.type === "hidden") input.remove()
        })
      form
        .querySelectorAll('input[name^="job[job_files_attributes]"][name$="[category]"]')
        .forEach((input) => {
          if (input.type === "hidden") input.remove()
        })
    }

    if (this.hasFileListTarget) {
      while (this.fileListTarget.firstChild) {
        this.fileListTarget.removeChild(this.fileListTarget.firstChild)
      }
    }

    this.hideMaxFilesError()
    this.showDropzone()
    this.dispatchFilesChanged()
  }

  dispatchFilesChanged() {
    this.dispatch("filesChanged", {
      detail: {
        count: this.uploadedFiles.length,
        files: this.uploadedFiles.map((upload) => ({
          name: upload.file.name,
          size: upload.file.size,
        })),
      },
    })
  }

  showDropzone() {
    this.progressTarget?.classList.add("hidden")
    this.processingTarget?.classList.add("hidden")
    this.successTarget?.classList.add("hidden")
    this.errorTarget?.classList.add("hidden")
    this.dropzoneTarget?.classList.remove("hidden")
  }

  showMaxFilesError() {
    if (!this.hasMaxFilesErrorTarget) return

    this.maxFilesErrorTarget.textContent = `Maximum ${this.maxFilesValue} files per upload`
    this.maxFilesErrorTarget.classList.remove("hidden")
  }

  hideMaxFilesError() {
    if (this.hasMaxFilesErrorTarget) {
      this.maxFilesErrorTarget.classList.add("hidden")
    }
  }

  uploadFile(file) {
    this.showProgress()

    const upload = new DirectUpload(file, this.urlValue, this)

    upload.create((error, blob) => {
      if (error) {
        this.showError("Upload failed. Please try again.")
        console.error("Upload error:", error)
      } else {
        this.showSuccess()
        this.createJobFileRecord(blob, file)
      }
    })
  }

  createJobFileRecord(blob, file) {
    this.showProcessing()

    const input = document.createElement("input")
    input.type = "hidden"
    input.name = "job[job_files_attributes][0][file]"
    input.value = blob.signed_id

    const form = this.element.closest("form")
    if (form) {
      form.appendChild(input)

      document.dispatchEvent(new CustomEvent("upload:done", {
        bubbles: true,
        detail: { blob, file },
      }))
    }
  }

  showProgress() {
    this.progressTarget?.classList.remove("hidden")
    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = "Just a moment while the BiddyAI™ does its thing"
    }
    this.errorTarget?.classList.add("hidden")
    this.successTarget?.classList.add("hidden")
  }

  showError(message) {
    if (this.hasErrorTarget) {
      const messageElement = this.errorTarget.querySelector(".text-sm")
      if (messageElement) {
        messageElement.textContent = message
      } else {
        this.errorTarget.textContent = message
      }
      this.errorTarget.classList.remove("hidden")
    }
    this.dropzoneTarget?.classList.add("hidden")
    this.droppingTarget?.classList.add("hidden")
    this.progressTarget?.classList.add("hidden")
  }

  showSuccess() {
    this.successTarget?.classList.remove("hidden")
    this.progressTarget?.classList.add("hidden")
  }

  showProcessing() {
    this.processingTarget?.classList.remove("hidden")
    this.progressTarget?.classList.add("hidden")
    this.successTarget?.classList.add("hidden")
  }

  directUploadWillCreateBlobWithXHR(xhr) {
    xhr.upload.addEventListener("progress", (event) => {
      const progress = event.loaded / event.total * 100
      if (this.hasProgressTextTarget) {
        this.progressTextTarget.textContent = `Uploading ${Math.round(progress)}%`
      }
    })
  }
}
